// Ported from dev-scrips-pm-react src/documents/components/code-picker.tsx (PROD-1119 documents module).
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import {
  useProcedureCodes,
  pickedCodeToData,
  type ProcedureCodeRow,
} from '@/documents/services/use-procedure-codes';
import { useRadiologyCodes } from '@/documents/services/use-radiology-codes';
import type { PatientDocumentData } from '@/documents/types/document';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * CodePicker — shared combobox for the Procedure + Radiology
 * document sub-forms (PROD-797 PR 4). The two flows only differ in
 * the master-data endpoint they query; the UI and field-mapping logic
 * are identical, so they share this component.
 *
 * Wire contract:
 *   - Input: a `PatientDocumentData` value (`{ title, code }` or null)
 *     stored on the document body's `data` field.
 *   - Output: same shape via `onChange`. When the user picks an item,
 *     we call `pickedCodeToData()` to translate the master row's
 *     `code.displayName / code.code` → `data.title / data.code`.
 *
 * Same trailing-edge debounce + enabled-gating pattern as
 * `LabRowEditor` — see `lab-row-editor.tsx` for the full contract.
 */

interface CodePickerProps {
  kind: 'procedure' | 'radiology';
  value: PatientDocumentData | null;
  onChange: (next: PatientDocumentData | null) => void;
  /**
   * Optional placeholder override. Defaults vary by `kind`
   * ("Search procedure name" / "Search radiology name") to match
   * Flutter's per-widget copy.
   */
  placeholder?: string;
}

export function CodePicker({ kind, value, onChange, placeholder }: CodePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 250);

  // Pick the right master query for the kind. Both hooks share the
  // same return shape so the rendering branch is identical.
  const procedureQuery = useProcedureCodes(kind === 'procedure' ? debounced : '');
  const radiologyQuery = useRadiologyCodes(kind === 'radiology' ? debounced : '');
  const query = kind === 'procedure' ? procedureQuery : radiologyQuery;

  const label = value?.title ?? value?.code ?? (placeholder ?? defaultPlaceholder(kind));

  const onSelect = (row: ProcedureCodeRow) => {
    const data = pickedCodeToData(row);
    onChange(data);
    setOpen(false);
    setSearch('');
  };

  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <AnchoredPopoverTrigger
        render={
          <button
            type="button"
            aria-label={kind === 'procedure' ? 'Procedure name' : 'Radiology name'}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-border-default bg-surface-card px-3 text-left text-sm',
              !value && 'text-text-hint',
            )}
          />
        }
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="size-4 text-text-secondary shrink-0 ml-2" aria-hidden />
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} className="w-100 p-0 gap-3" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={kind === 'procedure' ? 'Search procedures…' : 'Search radiology…'}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {debounced.trim().length === 0 ? (
              <CommandEmpty>
                Type to search the{' '}
                {kind === 'procedure' ? 'CPT' : 'radiology'} catalogue.
              </CommandEmpty>
            ) : query.isLoading ? (
              <CommandEmpty>Searching…</CommandEmpty>
            ) : query.isError ? (
              <CommandEmpty>Couldn't load codes.</CommandEmpty>
            ) : (query.data ?? []).length === 0 ? (
              <CommandEmpty>No matches. Try another word or a code.</CommandEmpty>
            ) : (
              <CommandGroup>
                {(query.data ?? []).map((row) => {
                  const code = row.code?.code ?? '';
                  // Procedure ships `displayName`, Radiology ships `display`
                  // (live-probed 2026-06-10) — read both or radiology rows
                  // render code-only.
                  const display =
                    row.code?.displayName ?? row.code?.display ?? '';
                  const isSelected = value?.code === code;
                  return (
                    <CommandItem
                      key={row.Id ?? row.specialitySkillId ?? code}
                      value={`${code} ${display}`}
                      onSelect={() => onSelect(row)}
                      data-checked={isSelected}
                    >
                      <span className="flex-1 truncate">
                        <span className="font-medium">{display}</span>
                        <span className="ml-2 text-xs text-text-muted">{code}</span>
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}

function defaultPlaceholder(kind: 'procedure' | 'radiology'): string {
  return kind === 'procedure' ? 'Search procedure name' : 'Search radiology name';
}
